import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ContextMenu from '../components/ContextMenu';

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [students, setStudents] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [stats, setStats] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    studentId: '',
    studentName: '',
    studentFees: '',
    tutorId: '',
    tutorName: '',
    tutorFees: '',
    dueDate: '',
    paymentDate: '',
    paymentStatus: 'Pending',
    paymentMode: '',
    commission: 0,
    tutorPayoutDate: '',
    tutorPayoutStatus: 'Pending',
    month: '',
    year: new Date().getFullYear(),
    parentMobileNumber: '',
    parentMobileNumber2: '',
    area: '',
    collectionType: '',
    tutorMobileNumber: '',
    tutorPaymentMode: '',
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchAccounts();
    fetchStudents();
    fetchTutors();
    fetchStats();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/accounts');
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get('/api/student-finals?limit=1000');
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchTutors = async () => {
    try {
      const response = await axios.get('/api/tutor-finals?limit=1000');
      setTutors(response.data.tutors || []);
    } catch (error) {
      console.error('Error fetching tutors:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/accounts/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Auto-fill student details when student is selected
    if (name === 'studentId') {
      const student = students.find(s => s._id === value);
      if (student) {
        setFormData(prev => ({
          ...prev,
          studentName: student.name,
          studentFees: student.feesPerMonth,
          tutorId: student.tutor?._id || student.tutor,
          tutorName: student.tutor?.name || student.tutorName,
          commission: student.commission || 0
        }));
        // Auto-fill tutor fees
        const tutor = tutors.find(t => t._id === (student.tutor?._id || student.tutor));
        if (tutor) {
          const studentRecord = tutor.students.find(s => s.studentId?.toString() === value);
          setFormData(prev => ({
            ...prev,
            tutorFees: studentRecord?.feesPerMonth || student.feesPerMonth - (student.commission || 0)
          }));
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      if (editingAccount) {
        await axios.put(`/api/accounts/${editingAccount._id}`, formData);
        setMessage('Account updated successfully!');
      } else {
        await axios.post('/api/accounts', formData);
        setMessage('Account created successfully!');
      }

      resetForm();
      fetchAccounts();
      fetchStats();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving account:', error);
      setMessage(error.response?.data?.message || 'Error saving account');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      studentId: account.studentId?._id || account.studentId,
      studentName: account.studentName,
      studentFees: account.studentFees,
      tutorId: account.tutorId?._id || account.tutorId,
      tutorName: account.tutorName,
      tutorFees: account.tutorFees,
      dueDate: account.dueDate?.split('T')[0] || '',
      paymentDate: account.paymentDate?.split('T')[0] || '',
      paymentStatus: account.paymentStatus,
      paymentMode: account.paymentMode || '',
      commission: account.commission,
      tutorPayoutDate: account.tutorPayoutDate?.split('T')[0] || '',
      tutorPayoutStatus: account.tutorPayoutStatus,
      month: account.month,
      year: account.year,
      parentMobileNumber: account.parentMobileNumber || '',
      parentMobileNumber2: account.parentMobileNumber2 || '',
      area: account.area || '',
      collectionType: account.collectionType || '',
      tutorMobileNumber: account.tutorMobileNumber || '',
      tutorPaymentMode: account.tutorPaymentMode || '',
      remarks: account.remarks || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account entry?')) return;

    try {
      await axios.delete(`/api/accounts/${id}`);
      setMessage('Account deleted successfully!');
      fetchAccounts();
      fetchStats();
    } catch (error) {
      console.error('Error deleting account:', error);
      setMessage('Error deleting account');
    }
  };

  const resetForm = () => {
    setFormData({
      studentId: '',
      studentName: '',
      studentFees: '',
      tutorId: '',
      tutorName: '',
      tutorFees: '',
      dueDate: '',
      paymentDate: '',
      paymentStatus: 'Pending',
      paymentMode: '',
      commission: 0,
      tutorPayoutDate: '',
      tutorPayoutStatus: 'Pending',
      month: '',
      year: new Date().getFullYear(),
      parentMobileNumber: '',
      parentMobileNumber2: '',
      area: '',
      collectionType: '',
      tutorMobileNumber: '',
      tutorPaymentMode: '',
      remarks: ''
    });
    setEditingAccount(null);
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="container" style={{ padding: '32px 24px' }}>
      <div className="page-header">
        <h1>Accounts Management</h1>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          + Add New Entry
        </button>
      </div>

      {message && (
        <div className={`alert ${message.includes('Error') ? 'alert-error' : 'alert-success'}`}>
          {message}
        </div>
      )}

      {/* Statistics */}
      {stats && (
        <div className="grid">
          <div className="card">
            <h3>Total Student Fees</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#48bb78' }}>₹{stats.totalStudentFees}</p>
          </div>
          <div className="card">
            <h3>Total Tutor Fees</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#f56565' }}>₹{stats.totalTutorFees}</p>
          </div>
          <div className="card">
            <h3>Total Commission</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>₹{stats.totalCommission}</p>
          </div>
          <div className="card">
            <h3>Pending Payments</h3>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>{stats.pendingPayments}</p>
          </div>
        </div>
      )}

      <div className="card">
        <h3>Accounts List ({accounts.length})</h3>
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Month/Year</th>
                  <th>Student</th>
                  <th>Student Fees</th>
                  <th>Tutor</th>
                  <th>Tutor Fees</th>
                  <th>Commission</th>
                  <th>Due Date</th>
                  <th>Payment Status</th>
                  <th>Payout Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>No accounts found</td>
                  </tr>
                ) : (
                  accounts.map((account) => (
                    <tr key={account._id}>
                      <td>{account.month} {account.year}</td>
                      <td>{account.studentName}</td>
                      <td>₹{account.studentFees}</td>
                      <td>{account.tutorName}</td>
                      <td>₹{account.tutorFees}</td>
                      <td>₹{account.commission}</td>
                      <td>{new Date(account.dueDate).toLocaleDateString()}</td>
                      <td>
                        <span className={`status-badge ${account.paymentStatus === 'Paid' ? 'status-delivered' : 'status-created'}`}>
                          {account.paymentStatus}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${account.tutorPayoutStatus === 'Paid' ? 'status-delivered' : 'status-created'}`}>
                          {account.tutorPayoutStatus}
                        </span>
                      </td>
                      <td className="actions">
                        <ContextMenu
                          onEdit={() => handleEdit(account)}
                          onDelete={() => handleDelete(account._id)}
                          itemName={account.student?.name || 'Account'}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <h2>{editingAccount ? 'Edit' : 'Add'} Account Entry</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label>Student *</label>
                  <select name="studentId" value={formData.studentId} onChange={handleChange} required>
                    <option value="">Select Student</option>
                    {students.map((student) => (
                      <option key={student._id} value={student._id}>
                        {student.name} - {student.class} ({student.subject})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Student Fees *</label>
                  <input type="number" name="studentFees" value={formData.studentFees} onChange={handleChange} min="0" required />
                </div>
                <div className="form-group">
                  <label>Tutor</label>
                  <input type="text" value={formData.tutorName} disabled />
                </div>
                <div className="form-group">
                  <label>Tutor Fees *</label>
                  <input type="number" name="tutorFees" value={formData.tutorFees} onChange={handleChange} min="0" required />
                </div>
                <div className="form-group">
                  <label>Commission</label>
                  <input type="number" name="commission" value={formData.commission} onChange={handleChange} min="0" />
                </div>
                <div className="form-group">
                  <label>Month *</label>
                  <select name="month" value={formData.month} onChange={handleChange} required>
                    <option value="">Select Month</option>
                    {months.map((month) => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Year *</label>
                  <input type="number" name="year" value={formData.year} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Due Date *</label>
                  <input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Payment Status</label>
                  <select name="paymentStatus" value={formData.paymentStatus} onChange={handleChange}>
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                    <option value="Overdue">Overdue</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Payment Date</label>
                  <input type="date" name="paymentDate" value={formData.paymentDate} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Payment Mode</label>
                  <select name="paymentMode" value={formData.paymentMode} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="Online">Online</option>
                    <option value="Cash">Cash</option>
                    <option value="Via Tutor">Via Tutor</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tutor Payout Status</label>
                  <select name="tutorPayoutStatus" value={formData.tutorPayoutStatus} onChange={handleChange}>
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tutor Payout Date</label>
                  <input type="date" name="tutorPayoutDate" value={formData.tutorPayoutDate} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Parent Mobile Number</label>
                  <input type="tel" name="parentMobileNumber" value={formData.parentMobileNumber} onChange={handleChange} placeholder="9876543210" />
                </div>
                <div className="form-group">
                  <label>Parent Mobile Number 2</label>
                  <input type="tel" name="parentMobileNumber2" value={formData.parentMobileNumber2} onChange={handleChange} placeholder="9876543210" />
                </div>
                <div className="form-group">
                  <label>Area</label>
                  <input type="text" name="area" value={formData.area} onChange={handleChange} placeholder="Wardhman Nagar" />
                </div>
                <div className="form-group">
                  <label>Collection Type</label>
                  <select name="collectionType" value={formData.collectionType} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="Advance">Advance</option>
                    <option value="End of Month">End of Month</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tutor Mobile Number</label>
                  <input type="tel" name="tutorMobileNumber" value={formData.tutorMobileNumber} onChange={handleChange} placeholder="9876543210" />
                </div>
                <div className="form-group">
                  <label>Tutor Payment Mode</label>
                  <select name="tutorPaymentMode" value={formData.tutorPaymentMode} onChange={handleChange}>
                    <option value="">Select</option>
                    <option value="Cash">Cash</option>
                    <option value="Online">Online</option>
                    <option value="Via Parent">Via Parent</option>
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>Remarks</label>
                <textarea name="remarks" value={formData.remarks} onChange={handleChange} rows="3" placeholder="Additional notes or remarks"></textarea>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounts;
